import { Eye } from "lucide-react";
import React, { useState } from "react";

const MyReportHTML = ({
  patientReport = {},
  opdForm = {},
  userDetails = {},
  btnTitle = "Generate PDF Report",
}) => {
  const [showReport, setShowReport] = useState(false);

  // Safe data getters with fallbacks
  const getPatientName = () => {
    return (
      opdForm?.userDetails?.name || userDetails?.userDetails?.name || "N/A"
    );
  };

  const getOpdNumber = () => {
    return patientReport?.opdNumber || "N/A";
  };

  const getAge = () => {
    return opdForm?.age ? `${opdForm.age} years` : "N/A";
  };

  const getGender = () => {
    return (
      opdForm?.userDetails?.gender || userDetails?.userDetails?.gender || "N/A"
    );
  };

  const getMedicalHistory = () => {
    const history =
      opdForm?.medicalDetails?.medicalHistory ||
      userDetails?.medicalDetails?.medicalHistory;
    if (Array.isArray(history) && history.length > 0) {
      return history.join(", ");
    }
    return "Not specified";
  };

  const getPhone = () => {
    return (
      opdForm?.userDetails?.contact?.mobile ||
      userDetails?.userDetails?.contact?.mobile ||
      "N/A"
    );
  };

  const getEmail = () => {
    return opdForm?.userDetails?.contact?.email || userDetails?.email || "N/A";
  };

  const getChiefComplaint = () => {
    return opdForm?.checkupInfo || "Not specified";
  };

  const getCurrentMedications = () => {
    console.log("Patient Report:", patientReport);
    const medications = patientReport?.examinationFindings?.currentMedications;
    if (Array.isArray(medications) && medications.length > 0) {
      return medications
        .map((med) => med || med?.label || med?.name || "Unknown")
        .join(", ");
    }
    return "N/A";
  };

  const getAllergies = () => {
    const allergies = patientReport?.examinationFindings?.allergies;
    if (Array.isArray(allergies) && allergies.length > 0) {
      return allergies
        .map(
          (a) =>
            `${a?.allergen || "Unknown"} (${a?.reaction || "N/A"}, ${
              a?.severity || "N/A"
            })`
        )
        .join(", ");
    }
    return "No allergies documented";
  };

  const getGumsCondition = () => {
    return patientReport?.examinationFindings?.gumsCondition || "Not specified";
  };

  const getOralHygieneLevel = () => {
    return (
      patientReport?.examinationFindings?.oralHygieneLevel || "Not specified"
    );
  };

  const getClinicalNotes = () => {
    return patientReport?.examinationFindings?.notes || "No additional notes";
  };

  const getSelectedTeeth = () => {
    const teeth = patientReport?.examinationFindings?.selectedTeeth;
    return Array.isArray(teeth) ? teeth : [];
  };

  const getPrimaryDiagnosis = () => {
    return patientReport?.diagnosis?.primaryDiagnosis || "Not specified";
  };

  const getSecondaryDiagnosis = () => {
    return patientReport?.diagnosis?.secondaryDiagnosis || "None";
  };

  const getTreatmentSuggested = () => {
    return patientReport?.treatmentPlan?.treatmentSuggested || "Not specified";
  };

  const getProceduresPerformed = () => {
    return patientReport?.treatmentPlan?.immediateProceduresPerformed || "None";
  };

  const getPrescribedMedications = () => {
    const medications = patientReport?.treatmentPlan?.prescribedMedications;
    return Array.isArray(medications) ? medications : [];
  };

  const getAdditionalNotes = () => {
    return patientReport?.additionalNotes || "";
  };

  const generatePatientReportHTML = () => {
    try {
      setShowReport(true);
    } catch (error) {
      console.error("Error showing report:", error);
    }
  };

  const closeReport = () => {
    try {
      setShowReport(false);
    } catch (error) {
      console.error("Error closing report:", error);
    }
  };


  const ReportContent = () => (
    <div
      id="patient-report-content"
      style={{
        fontFamily: "Arial, sans-serif",
        maxWidth: "800px",
        minWidth: "670px",
        margin: "0 auto",
        padding: "20px",
        backgroundColor: "white",
        color: "#333",
        fontSize: "11px",
        lineHeight: "1.4",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "20px",
          borderBottom: "3px solid #2563eb",
          paddingBottom: "15px",
        }}
      >
        {/* Header Image */}
        <div
          style={{
            marginBottom: "10px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/Mahalaxmi-header.jpg"
            alt="Mahalaxmi Dental Clinic"
            style={{
              width: "full",
              height: "auto",
              objectFit: "contain",
            }}
            onError={(e) => {
              e.target.style.display = "none";
              console.warn("Header image failed to load");
            }}
          />
        </div>
      </div>

      {/* Patient Information Section */}
      <div
        style={{
          background: "#f8fafc",
          border: "2px solid #e2e8f0",
          padding: "15px",
          marginBottom: "15px",
          borderRadius: "8px",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            fontSize: "16px",
            fontWeight: "bold",
            marginBottom: "12px",
            color: "#1f2937",
          }}
        >
          Patient Information
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
            fontSize: "10px",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                OPD Number:
              </span>
              <span style={{ color: "green", fontWeight: "bold" }}>{getOpdNumber()}</span>
            </div>
            
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Patient Name:
              </span>
              <span style={{ color: "#6b7280" }}>{getPatientName()}</span>
            </div>
            

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Age:
              </span>
              <span style={{ color: "#6b7280" }}>{getAge()}</span>
            </div>
             <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>Gender:</span>
              <span style={{ color: "#6b7280" }}>{getGender()}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Chief Complaint:
              </span>
              <span style={{ color: "green", fontWeight: "bold" }}>{getChiefComplaint()}</span>
            </div>
<div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Allergic to:
              </span>
              <span style={{ color: "#6b7280", textAlign: "center" }}>
                {getAllergies()}
              </span>
            </div>
           
          </div>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                OPD Date:
              </span>
              <span style={{ color: "#6b7280" }}>
                {new Date().toLocaleDateString("en-GB")}
              </span>
            </div>
           
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Phone:
              </span>
              <span style={{ color: "#6b7280" }}>{getPhone()}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Email/Username:
              </span>
              <span style={{ color: "#6b7280" }}>{getEmail()}</span>
            </div>
            
             <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Medical History:
              </span>
              <span style={{ color: "red" }}>{getMedicalHistory()}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0",
                borderBottom: "1px dotted #d1d5db",
              }}
            >
              <span style={{ fontWeight: "bold", color: "#374151" }}>
                Current Medications:
              </span>
              <span style={{ color: "#6b7280" }}>
                {getCurrentMedications()}
              </span>
            </div>
            
          </div>
        </div>
      </div>

      {/* Main Content - New Layout: 35% left, 65% right */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "35% 65%",
          gap: "15px",
          marginBottom: "20px",
          minHeight: "500px",
        }}
      >
        {/* Left Column (35%) - Clinical Examination and Diagnosis */}
        <div
          style={{
            border: "2px solid #e2e8f0",
            padding: "15px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            height: "610px",
          }}
        >
          <h3
            style={{
              textAlign: "left",
              fontSize: "14px",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f2937",
              textTransform: "uppercase",
              borderBottom: "2px solid #e2e8f0",
              paddingBottom: "8px",
            }}
          >
            Clinical Examination
          </h3>

          <div style={{ fontSize: "10px", flex: 1 }}>
            <div
              style={{
                marginBottom: "8px",
                padding: "6px",
                background: "#f9fafb",
                borderRadius: "4px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  color: "#374151",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Gums Condition:
              </span>
              <span style={{ color: "#6b7280" }}>{getGumsCondition()}</span>
            </div>

            <div
              style={{
                marginBottom: "8px",
                padding: "6px",
                background: "#f9fafb",
                borderRadius: "4px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  color: "#374151",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Oral Hygiene Level:
              </span>
              <span style={{ color: "#6b7280" }}>{getOralHygieneLevel()}</span>
            </div>

            <div
              style={{
                marginBottom: "8px",
                padding: "6px",
                background: "#f9fafb",
                borderRadius: "4px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  color: "#374151",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Clinical Notes:
              </span>
              <span style={{ color: "#6b7280" }}>{getClinicalNotes()}</span>
            </div>

            {getSelectedTeeth().length > 0 && (
              <div
                style={{
                  marginBottom: "8px",
                  padding: "6px",
                  background: "#f9fafb",
                  borderRadius: "4px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#374151",
                    display: "block",
                    marginBottom: "3px",
                  }}
                >
                  Selected Teeth for Treatment:
                </span>
                <div style={{ marginTop: "4px" }}>
                  {getSelectedTeeth()
                    .slice(0, 3)
                    .map((tooth, idx) => (
                      <div
                        key={idx}
                        style={{
                          fontSize: "9px",
                          marginBottom: "2px",
                          paddingLeft: "8px",
                          borderLeft: "2px solid #3b82f6",
                          color: "#6b7280",
                        }}
                      >
                        • Tooth {tooth?.id || "Unknown"}:{" "}
                        {tooth?.reasons &&
                          Array.isArray(tooth.reasons) &&
                          tooth.reasons.length > 0
                          ? tooth.reasons.join(", ")
                          : "No specific reasons"}

                        <p >• Diagnosis : {tooth?.diagnosis || "—"}</p>
                        <p >• Treatment : {tooth?.treatment || "—"}</p>

                      </div>
                    ))}
                  {getSelectedTeeth().length > 3 && (
                    <div
                      style={{
                        fontSize: "9px",
                        color: "#6b7280",
                        fontStyle: "italic",
                      }}
                    >
                      +{getSelectedTeeth().length - 3} more teeth
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Diagnosis Section */}
            <h3
              style={{
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "15px",
                marginBottom: "12px",
                color: "#1f2937",
                textTransform: "uppercase",
                borderBottom: "2px solid #e2e8f0",
                paddingBottom: "8px",
              }}
            >
              Diagnosis
            </h3>

            <div
              style={{
                marginBottom: "8px",
                padding: "6px",
                background: "#f9fafb",
                borderRadius: "4px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  color: "#374151",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Primary Diagnosis:
              </span>
              <span style={{ color: "#6b7280" }}>{getPrimaryDiagnosis()}</span>
            </div>

            <div
              style={{
                marginBottom: "8px",
                padding: "6px",
                background: "#f9fafb",
                borderRadius: "4px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  color: "#374151",
                  display: "block",
                  marginBottom: "3px",
                }}
              >
                Secondary Diagnosis:
              </span>
              <span style={{ color: "#6b7280" }}>
                {getSecondaryDiagnosis()}
              </span>
            </div>
          </div>

          {/* Treatment Plan */}
          <h3
            style={{
              textAlign: "left",
              fontSize: "14px",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f2937",
              textTransform: "uppercase",
              borderBottom: "2px solid #e2e8f0",
              paddingBottom: "8px",
            }}
          >
            Treatment Plan
          </h3>

          <div
            style={{
              marginBottom: "6px",
              padding: "6px",
              background: "#f9fafb",
              borderRadius: "4px",
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                color: "#374151",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Recommended Treatment:
            </span>
            <span style={{ color: "#6b7280" }}>{getTreatmentSuggested()}</span>
          </div>

          <div
            style={{
              marginBottom: "6px",
              padding: "6px",
              background: "#f9fafb",
              borderRadius: "4px",
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                color: "#374151",
                display: "block",
                marginBottom: "3px",
              }}
            >
              Procedures Performed:
            </span>
            <span style={{ color: "#6b7280" }}>{getProceduresPerformed()}</span>
          </div>
        </div>

        {/* Right Column (65%) - Prescribed Medications */}
        <div
          style={{
            border: "2px solid #e2e8f0",
            padding: "15px",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            height: "610px",
          }}
        >
          <h3
            style={{
              textAlign: "center",
              fontSize: "14px",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f2937",
              textTransform: "uppercase",
              borderBottom: "2px solid #e2e8f0",
              paddingBottom: "8px",
            }}
          >
            Prescribed Medications
          </h3>

          <div style={{ fontSize: "10px", flex: 1 }}>
            {getPrescribedMedications().length > 0 ? (
              <div style={{ marginBottom: "15px" }}>
                {/* Table Header */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.5fr 1fr",
                    gap: "1px",
                    backgroundColor: "#e2e8f0",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px 4px 0 0",
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      fontWeight: "bold",
                      backgroundColor: "#f8fafc",
                      borderRight: "1px solid #d1d5db",
                    }}
                  >
                    Medicine Name
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      fontWeight: "bold",
                      backgroundColor: "#f8fafc",
                      textAlign: "center",
                      borderRight: "1px solid #d1d5db",
                    }}
                  >
                    Frequency
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      fontWeight: "bold",
                      backgroundColor: "#f8fafc",
                      textAlign: "center",
                    }}
                  >
                    Duration
                  </div>
                </div>

                {/* Table Rows */}
                {getPrescribedMedications()
                  .filter((med) => med?.name)
                  .slice(0, 6)
                  .map((med, index) => {
                    const timeOfDayText =
                      med.timeOfDay && Array.isArray(med.timeOfDay)
                        ? med.timeOfDay.join(", ")
                        : "As needed";

                    const mealTimingText =
                      med.mealTiming === "before_meal"
                        ? "Before meal"
                        : med.mealTiming === "after_meal"
                        ? "After meal"
                        : "Not specified";

                    const mealTimes =
                      med.timeOfDay && Array.isArray(med.timeOfDay)
                        ? med.timeOfDay.length
                        : 1;
                    const totalDoses = med.durationDays
                      ? med.durationDays * mealTimes
                      : null;
                    const durationText = med.durationDays
                      ? `${med.durationDays} days${
                          totalDoses ? ` (${totalDoses} Tabs)` : ""
                        }`
                      : "As needed";

                    return (
                      <div
                        key={index}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1.5fr 1fr",
                          gap: "1px",
                          backgroundColor: "#e2e8f0",
                          border: "1px solid #d1d5db",
                          borderTop: "none",
                        }}
                      >
                        <div
                          style={{
                            padding: "8px",
                            backgroundColor: "white",
                            borderRight: "1px solid #d1d5db",
                            fontWeight: "500",
                          }}
                        >
                          {med.name}
                        </div>
                        <div
                          style={{
                            padding: "8px",
                            backgroundColor: "white",
                            borderRight: "1px solid #d1d5db",
                            textAlign: "center",
                            fontSize: "9px",
                          }}
                        >
                          <div
                            style={{ fontWeight: "500", marginBottom: "2px" }}
                          >
                            {timeOfDayText}
                          </div>
                          <div style={{ color: "#6b7280" }}>
                            ( {mealTimingText} )
                          </div>
                        </div>
                        <div
                          style={{
                            padding: "8px",
                            backgroundColor: "white",
                            textAlign: "center",
                            fontSize: "9px",
                          }}
                        >
                          {durationText}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div
                style={{
                  padding: "6px",
                  background: "#f9fafb",
                  borderRadius: "4px",
                  marginBottom: "15px",
                }}
              >
                <span style={{ color: "#6b7280" }}>
                  No medications prescribed
                </span>
              </div>
            )}

            {/* Additional Notes */}
            {getAdditionalNotes() && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "6px",
                  background: "#f9fafb",
                  borderRadius: "4px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#374151",
                    display: "block",
                    marginBottom: "3px",
                  }}
                >
                  Additional Notes:
                </span>
                <span style={{ color: "#6b7280", fontSize: "9px" }}>
                  {getAdditionalNotes().length > 100
                    ? `${getAdditionalNotes().substring(0, 100)}...`
                    : getAdditionalNotes()}
                </span>
              </div>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Generate Report Button */}
      <button
        onClick={generatePatientReportHTML}
        className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Eye />
        {btnTitle}
      </button>

      {/* Report Popup Modal */}
      {showReport && (
        <>
          {/* Print-specific styles */}
          <style>
            {`
              @media print {
                body * {
                  visibility: hidden;
                }
                #patient-report-content, #patient-report-content * {
                  visibility: visible;
                }
                #patient-report-content {
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                  height: 100%;
                  margin: 0;
                  padding: 15mm;
                  font-size: 11px !important;
                }
                @page {
                  size: A4;
                  margin: 10mm;
                  @top-left { content: ""; }
                  @top-center { content: ""; }
                  @top-right { content: ""; }
                  @bottom-left { content: ""; }
                  @bottom-center { content: ""; }
                  @bottom-right { content: ""; }
                }
                html {
                  -webkit-print-color-adjust: exact;
                }
              }
            `}
          </style>

          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Patient Medical Report
                </h2>
                <div className="flex items-center gap-3">
                  
                  <button
                    onClick={closeReport}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="overflow-y-auto max-h-[calc(95vh-70px)] bg-white">
                <ReportContent />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MyReportHTML;
